import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/authStore.js';
import { applySessionResultToCaches, invalidateProgress } from '../progress/hooks.js';
import { usePendingReviewStore } from './pendingReviewStore.js';
import { applyReviewsDirect, completeSessionDirect, CARD_STATES_KEY } from './hooks.js';
import {
  useQueueFlusher,
  drainEntries,
  isEligible,
  RETENTION_MS,
  SESSION_GRACE_MS,
  type FlushController,
} from '../lib/useQueueFlusher.js';

const BATCH_SIZE = 200; // RPC caps at 500; stay well under

/**
 * Mount once at the top of the authed tree (next to PendingQueueFlusher).
 * Drains the flashcard review queue batch-wise into `apply_flashcard_reviews`
 * (replay-idempotent) and pending session completions into
 * `complete_flashcard_session` (idempotent per session id).
 *
 * Retries never give up (reviews are precious scheduling data) but back off
 * exponentially per entry — see `useQueueFlusher` for why the time gate is
 * load-bearing.
 */
export function FlashcardFlusher() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const reviews = usePendingReviewStore((s) => s.reviews);
  const sessions = usePendingReviewStore((s) => s.sessions);
  const remove = usePendingReviewStore((s) => s.remove);
  const markAttempted = usePendingReviewStore((s) => s.markAttempted);
  const pruneOlderThan = usePendingReviewStore((s) => s.pruneOlderThan);

  const flush = useCallback(
    async (ctl: FlushController) => {
      if (!user) return;
      const now = Date.now();

      pruneOlderThan(now - RETENTION_MS);

      const myReviews = reviews.filter((r) => r.userId === user.id && isEligible(r, now));
      let fullyFlushed = false;
      if (myReviews.length > 0) {
        const batch = myReviews.slice(0, BATCH_SIZE);
        const ids = batch.map((r) => r.client_id);
        try {
          await applyReviewsDirect(batch);
          if (ctl.cancelled()) return;
          remove('reviews', ids);
          fullyFlushed = batch.length === myReviews.length;
        } catch {
          if (ctl.cancelled()) return;
          markAttempted('reviews', ids, now);
        }
      }

      const mySessions = sessions.filter(
        (s) =>
          s.userId === user.id &&
          isEligible(s, now) &&
          // Give the session's own direct mutation time to land first.
          now - s.queuedAt >= SESSION_GRACE_MS,
      );
      await drainEntries({
        entries: mySessions,
        send: (entry) =>
          completeSessionDirect({ sessionId: entry.session_id, cards: entry.cards }),
        onSent: (entry, result) => {
          remove('sessions', [entry.session_id]);
          applySessionResultToCaches(qc, user.id, result);
          invalidateProgress(qc, user.id);
        },
        onFailed: (entry) => markAttempted('sessions', [entry.session_id], now),
        cancelled: ctl.cancelled,
      });

      // Re-sync from the server only once NOTHING is left queued — otherwise
      // a refetch would clobber optimistic state for still-pending reviews.
      if (fullyFlushed && !ctl.cancelled()) {
        void qc.invalidateQueries({ queryKey: CARD_STATES_KEY(user.id) });
      }
    },
    [user, reviews, sessions, remove, markAttempted, pruneOlderThan, qc],
  );

  useQueueFlusher(user ? flush : null);
  return null;
}
